import Box from "@mui/material/Box"
import MenuItem from "@mui/material/MenuItem"
import OutlinedInput from "@mui/material/OutlinedInput"
import Select from "@mui/material/Select"
import { KeyboardArrowDownOutlined } from "@mui-symbols-material/w300"
import React, { useMemo } from "react"
import type { FieldValues, Path } from "react-hook-form"
import { Controller, useFormContext } from "react-hook-form"

import { TruncatedText } from "../TruncatedText"

import { FIELD_ERROR_CLASSES, FIELD_LABEL_CLASSES, MENU_PROPS } from "@/constants/forms"
import type { Location } from "@/graphql/codegen/graphql"
import { useGetLocations } from "@/hooks/useGetLocations"
import { useGetAssociatedLocations } from "@/screens/Companies/hooks"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/utils"

interface LocationSelectorProps<T extends FieldValues> {
  companyId?: string
  name: Path<T>
  label?: string
  locations?: Location[]
  disabled?: boolean
  placeholder?: string
}

const LocationSelector = <T extends FieldValues>({
  companyId,
  name,
  label = "Location",
  locations: providedLocations,
  disabled = false,
  placeholder = "Select One",
}: LocationSelectorProps<T>): React.ReactElement | null => {
  const methods = useFormContext<T>()
  const { locations: fetchedLocations } = useGetLocations(companyId || "")
  const { associatedLocationList } = useGetAssociatedLocations({ companyId: companyId || "" })

  const locations = useMemo(() => {
    if (providedLocations) {
      return providedLocations
    }

    const allBillingLocations = associatedLocationList.filter(
      (associatedLocation) => associatedLocation.billing?.locationId
    )
    return fetchedLocations.filter((location) =>
      allBillingLocations.some((associatedLocation) => associatedLocation.billing?.locationId === location.locationId)
    )
  }, [fetchedLocations, associatedLocationList, providedLocations])

  const getLocationLabel = (location: Location) => {
    return `${location.description} - ${formatLocationAddress(location, AddressFormat.AddressOnly)}`
  }

  const options = locations.map((location) => (
    <MenuItem key={location.locationId} value={location.locationId}>
      <TruncatedText text={getLocationLabel(location)} className='block truncate' />
    </MenuItem>
  ))

  return (
    <Controller
      name={name}
      control={methods.control}
      render={({ field, fieldState: { error } }) => (
        <Box className='mt-6'>
          <label htmlFor={name} className={FIELD_LABEL_CLASSES}>
            {label}
            <span className='text-red-600'>*</span>
          </label>
          <Select
            id={name}
            {...field}
            labelId={name}
            label='Select One'
            displayEmpty
            defaultValue={field.value}
            input={<OutlinedInput />}
            SelectDisplayProps={{
              className:
                "content-center h-6 px-4 py-2 focus:border-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-gray-200 text-sm leading-5",
            }}
            classes={{
              icon: "text-gray-600",
            }}
            IconComponent={KeyboardArrowDownOutlined}
            MenuProps={MENU_PROPS}
            variant='outlined'
            error={!!error}
            fullWidth
            disabled={disabled}
            renderValue={(key: string) => {
              if (!key) {
                return <span className='text-gray-500'>{placeholder}</span>
              }
              const selectedOption = locations.find(({ locationId }) => locationId === field.value)
              return selectedOption ? <TruncatedText text={getLocationLabel(selectedOption)} className='pr-4' /> : null
            }}
          >
            {options}
          </Select>
          {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
        </Box>
      )}
    />
  )
}

export default LocationSelector
